import React, { useEffect, useState } from "react";
import cn from "classnames";
import { createPortal } from "react-dom";
import { ReactComponent as CrossIcon } from "@icons/cross.svg";
import classNames from "classnames";

type ModalHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  title: string;
  hideDismiss?: boolean;
  close?: () => void;
};

const ModalHeader = ({ title, hideDismiss = false, close }: ModalHeaderProps): React.ReactElement => (
  <div className={cn("flex justify-between items-center font-bold rounded-t-lg pt-4 px-6", { "!pt-6 mb-6": !!title })}>
    <h2 className={cn("!pb-0 ")}>{title}</h2>
    {!hideDismiss && close && (
      <button aria-label="Close modal" type="button" onClick={close}>
        <CrossIcon aria-hidden className={cn("text-brand-purple h-4 w-4", title ? "text-brand-purple" : "text-brand-grey")} />
      </button>
    )}
  </div>
);

export type ModalBodyProps = React.HTMLAttributes<HTMLDivElement> & {
  children?: React.ReactNode;
};

export const ModalBody = ({ children, className, ...props }: ModalBodyProps) => {
  if (!children) {
    return null;
  }

  return (
    <div className={classNames("block h-auto px-6 my-6 bg-white", className)} {...props}>
      {children}
    </div>
  );
};

export type ModalFooterProps = React.HTMLAttributes<HTMLDivElement> & {
  className?: string;
  withoutBG?: boolean;
  children?: React.ReactNode;
};

export const ModalFooter = ({ className, withoutBG = false, children }: ModalFooterProps) => {
  if (!children) {
    return null;
  }
  return (
    <div
      className={cn(
        "flex flex-col sticky bottom-0 left-0 rounded-b-lg px-6 mb-6 my-8 gap-4",
        {
          "bg-white": withoutBG,
          "bg-neutral-100": !withoutBG,
        },
        className
      )}
    >
      {children}
    </div>
  );
};

export type widthTypes = "xs" | "sm" | "base" | "lg" | "xl";

export type ModalProps = React.HTMLAttributes<HTMLDivElement> & {
  close?: () => void;
  title?: string;
  allowOverflow?: boolean;
  bgDismiss?: boolean;
  width?: widthTypes;
  hideDismiss?: boolean;
  showDismissWithoutTitle?: boolean;
  children?: React.ReactNode;
};

export const Modal = ({
  close,
  title = "",
  allowOverflow = false,
  hideDismiss = false,
  showDismissWithoutTitle = false,
  bgDismiss = true,
  width = "base",
  children,
  ...props
}: ModalProps) => {
  const [createdElement, setCreatedElement] = useState<HTMLDivElement>();

  const dismiss = () => {
    if (bgDismiss && close) {
      close();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      dismiss();
      e.preventDefault();
    }
  };

  useEffect(() => {
    const keyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        dismiss();
        e.preventDefault();
      }
    };

    document.addEventListener("keydown", keyDown);

    const randomId = Math.random().toString();
    const wrapperElement = document.createElement("div");
    wrapperElement.setAttribute("id", `modal${randomId}`);
    document.body.appendChild(wrapperElement);
    setCreatedElement(wrapperElement);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", keyDown);
      wrapperElement?.parentNode?.removeChild(wrapperElement);
      document.body.style.overflow = "auto";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sizes = {
    xs: "w-[360px]",
    sm: "w-[540px]",
    base: "w-[740px]",
    lg: "w-[900px]",
    xl: "w-[1280px]",
  };

  const selectedWidth = sizes[width];

  if (!createdElement) {
    return null;
  }

  return createPortal(
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        onClick={dismiss}
        onKeyDown={handleKeyDown}
        {...props}
        data-testid="background"
        className="fixed flex inset-0 overscroll-none w-full z-[9998] bg-[#0f334d99] justify-center items-center cursor-default"
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={handleKeyDown}
          className={cn("relative m-6 h-auto transition-all bg-white shadow rounded-[20px] max-h-[90%]", selectedWidth, {
            "overflow-x-visible overflow-y-visible": allowOverflow,
            "overflow-x-hidden overflow-y-auto": !allowOverflow,
          })}
        >
          {(title || (showDismissWithoutTitle && !hideDismiss)) && <ModalHeader close={close} title={title} hideDismiss={hideDismiss} />}
          {children}
        </div>
      </div>
    </>,
    createdElement
  );
};
